import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Plane,
  ClipboardList,
  CreditCard,
  MessageSquare,
  Settings,
  Search,
  Menu,
  X,
  LogOut,
  Compass,
  Sparkles,
  BookOpen,
  FileText,
  PenSquare,
  Home,
  Map,
  UserCircle2,
  CalendarRange,
  Archive,
  FolderOpen,
  Library as LibraryIcon,
  Wallet,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { initials } from "../lib/format";
import { api } from "../lib/api";
import NotificationsBell from "../components/NotificationsBell";
import InstallPrompt from "../components/InstallPrompt";
import VersionPill from "./VersionPill";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import OfflineBanner from "../components/OfflineBanner";
import OnlineProvider from "../components/OnlineProvider";
import ThemeToggle from "../components/ThemeToggle";
import { OwnerImpersonateBanner } from "../components/OwnerImpersonateBar";
import { Toaster } from "sonner";
import useAdvisorLiveSync from "../hooks/useAdvisorLiveSync";
import { useScopedThemeEffect } from "../hooks/useTheme";

/**
 * Sidebar — editorial vertical nav for the portal.
 * Quiet, never crowded. Mono section labels. Active = serif text + gold rule.
 */
function NavGroup({ label, items, onItemClick }) {
  return (
    <div className="px-5">
      {label ? (
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-3">
          {label}
        </div>
      ) : null}
      <nav className="flex flex-col gap-0.5">
        {items.map(({ to, icon: Icon, label: itemLabel, end, badge }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onItemClick}
            data-testid={`portal-nav-${itemLabel.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-200 ${
                isActive
                  ? "text-[hsl(var(--foreground))] bg-[hsl(var(--background-paper))]"
                  : "text-[hsl(var(--foreground-secondary))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]/60"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  aria-hidden
                  className={`absolute left-0 top-2 bottom-2 w-px transition-all duration-300 ${
                    isActive ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                  }`}
                />
                <Icon className="size-[15px] opacity-80" strokeWidth={1.5} />
                <span
                  className={`text-[13px] tracking-tight flex-1 ${
                    isActive ? "font-serif" : "font-body"
                  }`}
                >
                  {itemLabel}
                </span>
                {badge > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[hsl(var(--accent-gold))] text-[#1A1A1A] font-mono text-[10px] font-medium"
                    data-testid={`portal-nav-badge-${itemLabel.toLowerCase()}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * ShortcutRow — quiet secondary links rendered as a compact inline row,
 * not a full NavGroup. Used for cross-Experience operational indexes
 * (Bookings, Payments) that stay accessible but don't claim primary
 * sidebar real estate.
 */
function ShortcutRow({ items, onItemClick }) {
  if (!items?.length) return null;
  return (
    <div className="px-5 -mt-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 pl-3 border-l border-[rgba(0,0,0,0.05)]">
        {items.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onItemClick}
            data-testid={`portal-nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                isActive
                  ? "text-[hsl(var(--accent-gold))]"
                  : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`
            }
          >
            <Icon className="size-3" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function PortalSidebar({ role, onNavigate }) {
  const [conciergeUnread, setConciergeUnread] = useState(0);
  const [pendingWishes, setPendingWishes] = useState(0);
  const [notifPriority, setNotifPriority] = useState(null); // urgent | attention | ambient | null — Wave 30.2 · Phase 8d: future use for terracotta urgent dot variant
  // eslint-disable-next-line no-unused-vars
  const _notifPriorityFuture = notifPriority;

  useEffect(() => {
    let mounted = true;
    const fetchCount = () => {
      if (role === "client") {
        // Wave 30.2 · Phase 8d — single calm aggregator drives the gold dot.
        api.get("/client-portal/notifications/summary")
          .then(({ data }) => {
            if (!mounted) return;
            // Total unread CONVERSATIONS (not message count) — restraint.
            const msgItems = (data?.items || []).filter((i) => i.kind === "message");
            setConciergeUnread(msgItems.length);
            setNotifPriority(data?.priority || null);
          })
          .catch(() => { if (mounted) { setConciergeUnread(0); setNotifPriority(null); } });
      } else {
        api.get("/concierge/conversations/unread-count")
          .then(({ data }) => { if (mounted) setConciergeUnread(data?.conversations || 0); })
          .catch(() => { if (mounted) setConciergeUnread(0); });
        api.get("/advisor/trip-requests?status=pending")
          .then(({ data }) => { if (mounted) setPendingWishes(Array.isArray(data) ? data.length : 0); })
          .catch(() => { if (mounted) setPendingWishes(0); });
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    const handler = () => fetchCount();
    window.addEventListener("verault:concierge-changed", handler);
    window.addEventListener("verault:trip-requests-changed", handler);
    // Wave 30.2 · Phase 8e — refetch when useLiveSync detects messages domain
    // change so the gold dot updates in lockstep with the calm sync taxonomy.
    window.addEventListener("vt:sync-changed:messages", handler);
    // Wave 30.3 · Phase 9d — advisor cockpit live-sync.
    // When the client takes an action, refresh badge counters in lockstep.
    window.addEventListener("vt:advisor-sync:message_sent", handler);
    window.addEventListener("vt:advisor-sync:travel_request_created", handler);
    window.addEventListener("vt:advisor-sync:approval_state_changed", handler);
    return () => {
      mounted = false;
      clearInterval(t);
      window.removeEventListener("verault:concierge-changed", handler);
      window.removeEventListener("verault:trip-requests-changed", handler);
      window.removeEventListener("vt:sync-changed:messages", handler);
      window.removeEventListener("vt:advisor-sync:message_sent", handler);
      window.removeEventListener("vt:advisor-sync:travel_request_created", handler);
      window.removeEventListener("vt:advisor-sync:approval_state_changed", handler);
    };
  }, [role]);

  // Wave 30.3 · Phase 9d — Mount the advisor live-sync feed once.
  // Quiet by default; toasts only fire for client-initiated events.
  useAdvisorLiveSync({ enabled: role === "advisor" || role === "owner" || role === "developer" });

  // Advisor sidebar — restructured into 3 editorial groups (O1):
  // OPERATIONS · PEOPLE · WORKSHOP. Bookings + Payments demoted to secondary
  // shortcuts under Operations (cross-Experience indexes only — the source
  // of truth lives inside each Experience detail). Standalone Journal +
  // Insights entries removed (folded into Content Studio + Overview).
  // Wave 36 · PI-8 — Sidebar restructure.
  // Operations groups the day-to-day surfaces. Treasury is promoted
  // from a muted shortcut to a primary Operations item alongside
  // Calendar so the firm-level financial snapshot is one click away.
  // Bookings stays as a muted shortcut — it's a per-trip sub-surface
  // most of the time, rarely a destination on its own. Payments link
  // is now subsumed by Treasury (per-trip Payments tab still exists
  // inside each Journey detail).
  const advisorOperations = [
    { to: "/portal/advisor", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/portal/advisor/journeys", icon: Plane, label: "Experiences" },
    { to: "/portal/advisor/planning", icon: Compass, label: "Design Studio" },
    { to: "/portal/advisor/calendar", icon: CalendarRange, label: "Calendar" },
    { to: "/portal/advisor/concierge", icon: Sparkles, label: "Concierge Desk", badge: conciergeUnread },
    { to: "/portal/advisor/treasury", icon: Wallet, label: "Treasury" },
  ];
  // Wave 36 · PI-9 — Bookings + Payments are no longer top-level
  // shortcuts. Bookings live inside each Trip Profile → Travel
  // Operations. Payments are absorbed into Treasury (firm + per-trip).
  const advisorOperationsShortcuts = [];
  const advisorPeople = [
    { to: "/portal/advisor/clients", icon: Users, label: "Clients" },
    { to: "/portal/advisor/trip-requests", icon: Sparkles, label: "Future wishes", badge: pendingWishes },
    { to: "/portal/advisor/forms", icon: FileText, label: "Forms" },
    { to: "/portal/advisor/agreements", icon: BookOpen, label: "Agreements" },
  ];
  const advisorWorkshop = [
    { to: "/portal/advisor/content", icon: PenSquare, label: "Content Studio" },
    { to: "/portal/advisor/content/media", icon: Map, label: "Image House" },
    { to: "/portal/advisor/library", icon: LibraryIcon, label: "My Collection" },
    { to: "/portal/advisor/files", icon: FolderOpen, label: "Dossiers" },
    { to: "/portal/advisor/intelligence", icon: Search, label: "House Intelligence" },
    { to: "/portal/advisor/records", icon: Archive, label: "Records" },
    { to: "/portal/advisor/settings", icon: Settings, label: "Settings" },
  ];

  // Client (Member Suite) — Wave 26.20 Phase 1a restructure.
  // 4 nav items in 3 quiet groups: Home + Experiences at the top
  // (no group label — the most-used items live alone), then a Support
  // group (Messages + Connect), then an Account group (Settings).
  // Documents / Travelers / Payments are now Experience-scoped tabs;
  // their routes stay reachable but they no longer clutter the
  // Suite-level sidebar.
  const clientPrimary = [
    { to: "/portal/client", icon: Home, label: "Home", end: true },
    { to: "/portal/client/experiences", icon: Compass, label: "Experiences" },
  ];
  const clientSupport = [
    { to: "/portal/client/messages", icon: MessageSquare, label: "Messages", badge: conciergeUnread },
    { to: "/portal/client/connect", icon: CalendarRange, label: "Connect" },
  ];
  const clientAccount = [
    { to: "/portal/client/settings", icon: Settings, label: "Settings" },
  ];

  return (
    <div className="flex flex-col gap-7 py-8">
      <Link
        to={role === "client" ? "/portal/client" : "/portal/advisor"}
        className="px-5 group block"
        onClick={onNavigate}
        data-testid="portal-brand-link"
        aria-label={role === "client" ? "VÉRAULT Travels — Member Suite home" : "VÉRAULT Travels — Studio home"}
      >
        {/* Phase 1c · Branding lockup matches the public website EXACTLY
            (PublicNav.jsx line 28). One serif wordmark, one ink color,
            one tracking — no portal-specific variants. The Member Suite
            and Studio sub-labels live in a separate, quieter mono line
            below, so the master brand reads identically across the
            public site, advisor portal, and client portal. */}
        <div className="font-serif text-xl tracking-wide text-[#1A1A1A]">
          V&Eacute;RAULT Travels
        </div>
        <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          {role === "client" ? "Member Suite" : "Studio"}
        </div>
      </Link>

      {role === "client" ? (
        <>
          {/* Phase 1a — Home + Experiences live with NO group label
              (the most-used items breathe). Then Support and Account. */}
          <NavGroup label={null} items={clientPrimary} onItemClick={onNavigate} />
          <NavGroup label="Support" items={clientSupport} onItemClick={onNavigate} />
          <NavGroup label="Account" items={clientAccount} onItemClick={onNavigate} />
        </>
      ) : (
        <>
          <NavGroup label="Operations" items={advisorOperations} onItemClick={onNavigate} />
          {advisorOperationsShortcuts.length > 0 && (
            <ShortcutRow items={advisorOperationsShortcuts} onItemClick={onNavigate} />
          )}
          <NavGroup label="People" items={advisorPeople} onItemClick={onNavigate} />
          <NavGroup label="Workshop" items={advisorWorkshop} onItemClick={onNavigate} />
        </>
      )}
    </div>
  );
}

export function PortalTopBar({ onOpenSearch, onOpenSidebar, role }) {
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();
  const initial = initials(user?.name || user?.email || "");
  const isAdvisor = role !== "client";

  const handleLogout = async () => {
    // Feb 2026 — two-step exit for clients:
    //   portal  → click "exit"  → return to /suite/{slug}/welcome (foyer)
    //                              WITHOUT clearing the session.
    //   foyer   → click "exit"  → full logout → /suite/{slug}/access.
    //
    // The slug is captured by SuiteLandingPublic into localStorage when
    // the client lands on /welcome, so we can route back to *their* foyer
    // (not a hardcoded one).
    const wasClient = (user?.role || "").toLowerCase() === "client";
    if (wasClient) {
      let slug = "";
      try { slug = window.localStorage.getItem("verault_suite_slug") || ""; } catch { /* noop */ }
      // Soft exit: keep the session, drop them on the foyer.
      navigate(`/suite/${slug || "jackson"}/welcome`, { replace: true });
      return;
    }
    // Advisor: full sign-out to the studio door (unchanged).
    navigate("/owner-access", { replace: true });
    try {
      await logout?.();
    } catch (_e) { /* noop */ }
  };

  return (
    <div className="sticky top-0 z-30 backdrop-blur-xl bg-[hsl(var(--background))]/75 border-b border-[hsl(var(--surface-hairline))]/60">
      <div className="flex items-center gap-3 px-5 md:px-8 h-[52px]">
        <button
          onClick={onOpenSidebar}
          className="md:hidden inline-flex items-center justify-center size-9 rounded-md text-[hsl(var(--foreground-secondary))] hover:bg-[hsl(var(--surface-hover))]"
          data-testid="portal-mobile-menu"
          aria-label="Open menu"
        >
          <Menu className="size-4" strokeWidth={1.5} />
        </button>

        {/* Wave 30.4 — Search refined: lighter height (h-8), softer
            border, no shadow. Mobile collapses to icon-only trigger. */}
        <button
          onClick={onOpenSearch}
          data-testid="portal-open-search"
          className="hidden sm:flex flex-1 max-w-md items-center gap-2.5 h-8 px-3 rounded-full bg-[hsl(var(--background-paper))]/70 hover:bg-[hsl(var(--surface-hover))] border border-[hsl(var(--surface-hairline))]/40 text-left transition-colors"
        >
          <Search className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <span className="font-body text-[12.5px] text-[hsl(var(--foreground-muted))]">
            {role === "client" ? "Search your trip…" : "Search clients, trips, bookings…"}
          </span>
          <kbd className="ml-auto font-mono text-[9.5px] tracking-wider text-[hsl(var(--foreground-muted))] border border-[hsl(var(--surface-hairline))]/50 rounded px-1.5 py-0.5">
            ⌘K
          </kbd>
        </button>
        {/* Mobile: icon-only search trigger */}
        <button
          onClick={onOpenSearch}
          data-testid="portal-open-search-mobile"
          className="sm:hidden inline-flex items-center justify-center size-9 rounded-full text-[hsl(var(--foreground-secondary))] hover:bg-[hsl(var(--surface-hover))]"
          aria-label="Open search"
        >
          <Search className="size-4" strokeWidth={1.5} />
        </button>

        <div className="ml-auto flex items-center gap-1.5">
          <ThemeToggle scope={role === "client" ? "client" : "advisor"} />
          {isAdvisor && <NotificationsBell />}
          <div
            className="hidden sm:flex items-center justify-center size-9 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] font-mono text-[11px] tracking-wider"
            title={user?.name || user?.email}
            data-testid="portal-user-avatar"
          >
            {initial || "·"}
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center justify-center size-9 rounded-md text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--surface-hover))]"
            data-testid="portal-logout"
            title={role === "client" ? "Return to the foyer" : "Sign out"}
            aria-label={role === "client" ? "Return to the foyer" : "Sign out"}
          >
            <LogOut className="size-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * PortalShell — the foundation layout for /portal/* routes.
 * Calm warm cream background, editorial sidebar, sticky top bar,
 * generous content gutter. Replaces legacy `components/portal/PortalShell.jsx`.
 */
export function PortalShell({ role = "advisor", children }) {
  const [openMobile, setOpenMobile] = useState(false);
  const location = useLocation();

  // Wave 30.4.3 — Per-surface theme scoping.
  // Apply this portal's persisted theme on mount; remove `data-theme`
  // when this shell unmounts so the public site is never affected.
  // `client` → vt-theme-client · `advisor` → vt-theme-advisor.
  // Both `owner` and `developer` roles share the advisor scope.
  const themeScope = role === "client" ? "client" : "advisor";
  useScopedThemeEffect(themeScope);

  // close mobile drawer on route change
  React.useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname]);

  // Feb 2026 — remember the last client portal location so the foyer
  // can offer a "Continue where you left off" shortcut on return.
  React.useEffect(() => {
    if (role !== "client") return;
    const path = location.pathname + (location.search || "");
    if (path && path.startsWith("/portal/client")) {
      try { window.localStorage.setItem("verault_last_path", path); } catch { /* noop */ }
    }
  }, [location.pathname, location.search, role]);

  // Wave 11u · Batch D — Pull-to-refresh (mobile gesture). The hook
  // auto-disables on desktop / non-touch devices. On refresh, broadcasts
  // a `vt:pull-refresh` event that pages can listen to + reload data.
  // We also do a hard `location.reload()` after a tiny delay so any
  // server-rendered state and SWR cache stays fresh.
  const { bindRef, indicator } = usePullToRefresh(async () => {
    window.dispatchEvent(new CustomEvent("vt:pull-refresh"));
    await new Promise((r) => setTimeout(r, 250));
    // Refresh only the current page (no full-page nav).
    // SWR caches and React Query (if any) self-revalidate via the event.
    // For simplicity + safety we keep `location.reload(true)` deferred to
    // a future iteration when we audit which pages have side-effects.
  });

  const openSearch = () => {
    // Wire to existing CommandPalette via custom event (mounted in App.js).
    window.dispatchEvent(new CustomEvent("verault:open-command-palette"));
  };

  return (
    <OnlineProvider>
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      {/* Wave 30.6 · Phase 11b — Skip-to-content link for keyboard
          + screen-reader users. Visually hidden until focused. */}
      <a
        href="#portal-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:px-3 focus:py-2 focus:rounded-md focus:bg-[hsl(var(--foreground))] focus:text-[hsl(var(--background))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-gold))]"
        data-testid="skip-to-content"
      >
        Skip to content
      </a>
      <OfflineBanner />
      <OwnerImpersonateBanner />
      <div className="flex">
        {/* Desktop sidebar */}
        <aside
          className="hidden md:block w-[240px] shrink-0 border-r border-[rgba(0,0,0,0.05)] sticky top-0 h-screen overflow-y-auto"
          data-testid="portal-sidebar-desktop"
          aria-label="Primary navigation"
        >
          <PortalSidebar role={role} />
        </aside>

        {/* Mobile sidebar drawer */}
        {openMobile && (
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            onClick={() => setOpenMobile(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div
              className="absolute left-0 top-0 bottom-0 w-[260px] bg-[hsl(var(--background))] shadow-floating"
              onClick={(e) => e.stopPropagation()}
              data-testid="portal-sidebar-mobile"
            >
              <button
                onClick={() => setOpenMobile(false)}
                className="absolute right-2 top-2 size-11 inline-flex items-center justify-center rounded-md text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--background-paper))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent-gold))]"
                aria-label="Close menu"
              >
                <X className="size-4" strokeWidth={1.5} />
              </button>
              <PortalSidebar role={role} onNavigate={() => setOpenMobile(false)} />
            </div>
          </div>
        )}

        <main
          id="portal-main"
          className="flex-1 min-w-0"
          tabIndex={-1}
          ref={(el) => bindRef?.(el)}
        >
          {indicator}
          <PortalTopBar
            role={role}
            onOpenSearch={openSearch}
            onOpenSidebar={() => setOpenMobile(true)}
          />
          <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-12 max-w-[1280px]">
            {children}
          </div>
        </main>
      </div>
      {/* Wave 27.5 · Phase 4e — Sonner toaster for in-portal modals
          (e-sign · upload · form submission · live-update sync).
          Wave 30.6 · Phase 11b — toaster lives in aria-live region for
          screen-reader announcements. */}
      <Toaster position="top-center" richColors closeButton toastOptions={{ ariaProps: { role: "status", "aria-live": "polite" } }} />
      {/* Wave 30 · Phase 7a — Install prompt (client-only, deferred 20s). */}
      {role === "client" ? <InstallPrompt /> : null}
      {/* Wave 12.v — Floating version pill (owner/advisor/developer only). */}
      <VersionPill />
    </div>
    </OnlineProvider>
  );
}
